import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Search,
  Filter,
  Download,
  MoreHorizontal,
  ShoppingCart,
  Calendar,
  User,
  Package,
  CreditCard,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ChevronDown,
  Eye,
  Edit2,
  Trash2,
} from 'lucide-react'
import { apiService } from '../services/api'
import { useToast } from '../hooks/useToast'
import type { Order } from '../types'
import Button from '../components/common/Button'
import Input from '../components/common/Input'
import Card from '../components/common/Card'
import Modal from '../components/common/Modal'
import Loading from '../components/common/Loading'

const statusConfig = {
  pending: { label: '待处理', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400', icon: Clock },
  processing: { label: '处理中', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400', icon: Package },
  completed: { label: '已完成', color: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400', icon: CheckCircle },
  cancelled: { label: '已取消', color: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400', icon: XCircle },
  refunded: { label: '已退款', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400', icon: CreditCard },
  disputed: { label: '争议中', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400', icon: AlertCircle },
}

const paymentStatusConfig = {
  pending: { label: '待支付', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400' },
  paid: { label: '已支付', color: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' },
  failed: { label: '支付失败', color: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400' },
  refunded: { label: '已退款', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400' },
}

export default function OrderManagement() {
  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all')
  const [selectedOrders, setSelectedOrders] = useState<number[]>([])
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [newStatus, setNewStatus] = useState('')
  const { showToast } = useToast()

  useEffect(() => {
    fetchOrders()
  }, [])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const response = await apiService.getOrders()
      if (response.data && Array.isArray(response.data)) {
        setOrders(response.data)
      } else if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
        setOrders(response.data.data)
      } else {
        setOrders([])
      }
    } catch (error) {
      showToast('获取订单列表失败', 'error')
      setOrders([])
    } finally {
      setLoading(false)
    }
  }

  const filteredOrders = orders.filter((order) => {
    // Search in order data
    const searchLower = searchQuery.toLowerCase()
    const matchesSearch = searchQuery === '' || 
      order.id.toString().includes(searchLower) ||
      JSON.stringify(order.data).toLowerCase().includes(searchLower)
    
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter
    const matchesPaymentStatus = paymentStatusFilter === 'all' || order.payment_status === paymentStatusFilter
    
    return matchesSearch && matchesStatus && matchesPaymentStatus
  })

  const handleUpdateStatus = async () => {
    if (!selectedOrder || !newStatus) return
    try {
      await apiService.updateOrderStatus(selectedOrder.id, newStatus)
      showToast('订单状态更新成功', 'success')
      setIsStatusModalOpen(false)
      setSelectedOrder(null)
      setNewStatus('')
      fetchOrders()
    } catch (error) {
      showToast('订单状态更新失败', 'error')
    }
  }

  const handleBulkUpdateStatus = async (status: string) => {
    try {
      await Promise.all(selectedOrders.map(id => apiService.updateOrderStatus(id, status)))
      showToast('批量更新成功', 'success')
      setSelectedOrders([])
      fetchOrders()
    } catch (error) {
      showToast('批量更新失败', 'error')
    }
  }

  const handleExportOrders = async () => {
    try {
      const response = await apiService.exportData('orders', 'csv')
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank')
        showToast('订单导出成功', 'success')
      } else {
        showToast('订单导出失败', 'error')
      }
    } catch (error) {
      showToast('订单导出失败', 'error')
    }
  }

  const openDetailModal = (order: Order) => {
    setSelectedOrder(order)
    setIsDetailModalOpen(true)
  }

  const openStatusModal = (order: Order) => {
    setSelectedOrder(order)
    setNewStatus(order.status)
    setIsStatusModalOpen(true)
  }

  const toggleOrderSelection = (orderId: number) => {
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    )
  }

  const toggleAllSelection = () => {
    if (selectedOrders.length === filteredOrders.length) {
      setSelectedOrders([])
    } else {
      setSelectedOrders(filteredOrders.map((o) => o.id))
    }
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">订单管理</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">查看和管理所有订单</p>
        </div>
        <div className="flex items-center gap-3">
          {selectedOrders.length > 0 && (
            <select
              onChange={(e) => e.target.value && handleBulkUpdateStatus(e.target.value)}
              className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:text-white"
              defaultValue=""
            >
              <option value="" disabled>批量更新状态</option>
              <option value="pending">待处理</option>
              <option value="processing">处理中</option>
              <option value="completed">已完成</option>
              <option value="cancelled">已取消</option>
              <option value="refunded">已退款</option>
            </select>
          )}
          <Button
            variant="secondary"
            onClick={handleExportOrders}
          >
            <Download className="h-4 w-4 mr-2" />
            导出订单
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="搜索订单号或订单数据..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:text-white"
          >
            <option value="all">所有状态</option>
            <option value="pending">待处理</option>
            <option value="processing">处理中</option>
            <option value="completed">已完成</option>
            <option value="cancelled">已取消</option>
            <option value="refunded">已退款</option>
            <option value="disputed">争议中</option>
          </select>
          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:text-white"
          >
            <option value="all">所有支付状态</option>
            <option value="pending">待支付</option>
            <option value="paid">已支付</option>
            <option value="failed">支付失败</option>
            <option value="refunded">已退款</option>
          </select>
        </div>
      </Card>

      {/* Orders Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={
                      selectedOrders.length === filteredOrders.length &&
                      filteredOrders.length > 0
                    }
                    onChange={toggleAllSelection}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">订单号</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">表单</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">金额</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">状态</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">支付</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">日期</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-700 dark:text-gray-300">操作</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {filteredOrders.map((order) => {
                  const StatusIcon = statusConfig[order.status as keyof typeof statusConfig]?.icon || Clock
                  return (
                    <motion.tr
                      key={order.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order.id)}
                          onChange={() => toggleOrderSelection(order.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-gray-900 dark:text-white">
                          #{order.id}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          Form #{order.form_id}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900 dark:text-white">
                          ¥{order.amount?.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs ${
                            statusConfig[order.status as keyof typeof statusConfig]?.color ||
                            'bg-gray-100 text-gray-700'
                          }`}
                        >
                          <StatusIcon className="h-3 w-3" />
                          {statusConfig[order.status as keyof typeof statusConfig]?.label ||
                            order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs ${
                            paymentStatusConfig[
                              order.payment_status as keyof typeof paymentStatusConfig
                            ]?.color || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {paymentStatusConfig[
                            order.payment_status as keyof typeof paymentStatusConfig
                          ]?.label || order.payment_status || 'pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {order.created_at ? new Date(order.created_at).toLocaleDateString('zh-CN') : '-'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openDetailModal(order)}
                            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-blue-600 dark:hover:bg-gray-700"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openStatusModal(order)}
                            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-blue-600 dark:hover:bg-gray-700"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  )
                })}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {filteredOrders.length === 0 && (
          <div className="py-12 text-center">
            <ShoppingCart className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">暂无订单</h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">订单将显示在这里</p>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false)
          setSelectedOrder(null)
        }}
        title={`订单详情 - #${selectedOrder?.id}`}
      >
        {selectedOrder && (
          <div className="space-y-6">
            {/* Order Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">订单信息</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">订单 #{selectedOrder.id}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">表单 #{selectedOrder.form_id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">创建时间</label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString('zh-CN') : '-'}
                </p>
              </div>
            </div>

            {/* Order Status */}
            <div className="flex items-center gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">订单状态</label>
                <span
                  className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs ${
                    statusConfig[selectedOrder.status as keyof typeof statusConfig]?.color
                  }`}
                >
                  {statusConfig[selectedOrder.status as keyof typeof statusConfig]?.label}
                </span>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">支付状态</label>
                <span
                  className={`mt-1 inline-flex items-center rounded-full px-2 py-1 text-xs ${
                    paymentStatusConfig[
                      selectedOrder.payment_status as keyof typeof paymentStatusConfig
                    ]?.color || 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {paymentStatusConfig[
                    selectedOrder.payment_status as keyof typeof paymentStatusConfig
                  ]?.label || selectedOrder.payment_status || 'pending'}
                </span>
              </div>
            </div>

            {/* Order Data */}
            <div>
              <label className="text-sm font-medium text-gray-500 dark:text-gray-400">订单数据</label>
              <div className="mt-2 space-y-2">
                {Object.entries(selectedOrder.data || {}).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex items-center justify-between rounded-lg bg-gray-50 dark:bg-gray-800 p-3"
                  >
                    <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">{key}</span>
                    <span className="text-sm text-gray-900 dark:text-white">
                      {Array.isArray(value) ? value.join(', ') : typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex justify-between">
                <span className="font-medium text-gray-900 dark:text-white">总计</span>
                <span className="font-bold text-lg text-gray-900 dark:text-white">
                  ¥{selectedOrder.amount?.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Notes */}
            {selectedOrder.notes && (
              <div>
                <label className="text-sm font-medium text-gray-500 dark:text-gray-400">订单备注</label>
                <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{selectedOrder.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Status Update Modal */}
      <Modal
        isOpen={isStatusModalOpen}
        onClose={() => {
          setIsStatusModalOpen(false)
          setSelectedOrder(null)
          setNewStatus('')
        }}
        title="更新订单状态"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              选择新状态
            </label>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:text-white"
            >
              <option value="pending">待处理</option>
              <option value="processing">处理中</option>
              <option value="completed">已完成</option>
              <option value="cancelled">已取消</option>
              <option value="refunded">已退款</option>
              <option value="disputed">争议中</option>
            </select>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsStatusModalOpen(false)
                setSelectedOrder(null)
                setNewStatus('')
              }}
            >
              取消
            </Button>
            <Button onClick={handleUpdateStatus}>
              确认更新
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
